import React, {useEffect, useMemo, useState} from "react";

import {useGlobalState} from "../../GlobalState";
import ArtificialIntelligenceIcon from "../../pages/Rates/RatePlanner/ArtificialIntelligenceIcon";
import useDialog from "../../hooks/useDialog";

import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItem from "@mui/material/ListItem";
import VerifiedIcon from '@mui/icons-material/Verified';
import NewFeatureDialog from "./NewFeatureDialog";
import Avatar from "@mui/material/Avatar";
import {useTheme} from "@mui/material";
import Divider from "@mui/material/Divider"
import List from "@mui/material/List";
import PetIcon from '@mui/icons-material/Pets';
import EmailIcon from '@mui/icons-material/Email';
import TaskIcon from "@mui/icons-material/Task";
import ArticleIcon from "@mui/icons-material/Article";


// Check if a version  is more recent than another.
// the comparison is done only if both the version string are
// valid string: major.minor.patch[-string]
// The comparison is done by looking only at major, minor and patch
// component additional -rc1, -rc2, are not considered
const moreRecentThan = (version1, version2) => {
    // Check if either version is null or empty
    if (!version1 || !version2) {
        return false;
    }

    // Validate version format
    if (!isValidVersionFormat(version1) || !isValidVersionFormat(version2)) {
        return false;
    }

    // Split version strings into components
    const components1 = version1.split('.');
    const components2 = version2.split('.');

    // Compare individual components
    for (let i = 0; i < Math.max(components1.length, components2.length); i++) {
        const num1 = parseInt(components1[i]) || 0;
        const num2 = parseInt(components2[i]) || 0;

        if (num1 > num2) {
            return true;
        } else if (num1 < num2) {
            return false;
        }
    }

    // All components are equal
    return false;
}

const isValidVersionFormat = (versionString) => {
    // Check if the given version string is like major.minor.patch-rc or major.minor.patch-sub or major.minor.patch
    return /^\d+\.\d+\.\d+(-[a-zA-Z0-9]+)?$/.test(versionString);
}

const FeaturesNotifier = ({children}) => {
    const currentVersion = process.env.REACT_APP_VERSION;
    const {settings, updateSettings} = useGlobalState();
    const dialog = useDialog();
    const lastVersionNotified = settings?.lastVersionNotified || '0.0.0';
    const theme = useTheme();

    // Set the content of the dialog in a state to avoid it changes
    // when the version is updated.
    const [dialogContent, setDialogContent] = useState([]);

    // array of features sorted from the newest to the oldest
    const features = useMemo(() => [
        {
            version: '1.19.0',
            messageEl: <Stack alignItems="center">
                <Typography variant="h6">Riepilogo Costi e Pagamenti prenotazione</Typography>
                <Avatar sx={{my: 1, width: 100, height: 100, backgroundColor: theme.palette.primary.main}}><ArticleIcon sx={{fontSize: '80px'}}/></Avatar>
                <Typography mt={2}>Sono state introdotte le seguenti funzionalità per migliorare la gestione dei costi e dei pagamenti associati alle prenotazioni:</Typography>
                <List>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Possibilità di creare documenti di riepilogo Costi e Pagamenti in PDF da consegnare agli ospiti</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Possibilità di aggiungere costi extra alle prenotazioni</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Possibilità di indicare l'importo della tassa di soggiorno per una prenotazione</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Possibilità di inserire uno sconto e un deposito cauzionale</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Calcolo automatico della tassa di soggiorno a partire dai dati presenti (numero ospiti o check-in online o check-in effettivi)</ListItem>
                </List>
                <Typography mt={2} fontWeight={600}>Altri miglioramenti:</Typography>
                <List>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Per ogni struttura è possibile indicare le regole della casa e le istruzioni per il WI-FI (presentate nel documento di riepilogo costi/pagamenti)</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Per ogni nuovo check-in è possibile indicare la data prevista di partenza (utilizzata nelle schedine del servizio Alloggiati)</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Possibilità di modificare i dati associati a prenotazioni passate</ListItem>
                </List>
            </Stack>
        },
        {
            version: '1.18.0',
            messageEl: <Stack alignItems="center">
                <Typography variant="h6">Condizioni e Documento di Conferma Prenotazione</Typography>
                <Avatar sx={{my: 1, width: 100, height: 100, backgroundColor: theme.palette.primary.main}}><TaskIcon sx={{fontSize: '80px'}}/></Avatar>
                <Typography mt={2}>Sono state introdotte le seguenti funzionalità:</Typography>
                <List>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Possibilità di indicare le condizioni della prenotazione (politiche di conferma, cancellazione, e tassa di soggiorno)</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Possibilità di indicare i vincoli di orario per il check-in e il check-out delle prenotazioni</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Possibilità  di creare documenti di conferma prenotazione</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Possibilità di inviare via email e direttamente dall'app i documenti di conferma agli ospiti</ListItem>
                    </List>
            </Stack>
        },
        {
            version: '1.10.1',
            messageEl: <Stack alignItems="center">
                <Typography variant="h6">Risposte automatiche</Typography>
                <Avatar sx={{my: 1, width: 100, height: 100, backgroundColor: theme.palette.primary.main}}><EmailIcon sx={{fontSize: '80px'}}/></Avatar>
                <List>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Crea e invia preventivi in automatico ed entro pochi minuti dalla richiesta di prenotazione</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>I prezzi sono calcolati in base alle tue tariffe e diponibilità</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>In caso di mancata disponibilità saranno proposte date alternative</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Per utilizzarlo deve essere attivato nelle Impostazioni</ListItem>
                </List>
                <Typography mt={2}>Le richieste di prenotazione che contengono delle richieste specifiche da parte dell'ospite non saranno processate in automatico ma inoltrate alla tua email.</Typography>
            </Stack>
        },
        {
            version: '1.8.0',
            messageEl: <Stack alignItems="center">
                <Typography variant="h6">Animali Domestici</Typography>
                <Avatar sx={{my: 1, width: 100, height: 100, backgroundColor: theme.palette.primary.main}}><PetIcon sx={{fontSize: '80px'}}/></Avatar>
                <List>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>E' stato aggiunto il numero di animali domestici alle richieste di prenotazione</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Nella sezione Filtri Richieste è possibile indicare il numero massimo di animali domestici ammessi</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Nei piani tariffari è possibili indicare se si accettano animali domestici (di default sono accettati) e il supplemento per ogni animale domestico (di default 0 euro)</ListItem>
                </List>
            </Stack>
        },
        {
            version: '1.7.0',
            messageEl: <Stack alignItems="center">
                <Typography variant="h6">Prezzi smart</Typography>
                <Avatar sx={{my: 1, width: 100, height: 100, backgroundColor: theme.palette.primary.main}}><ArtificialIntelligenceIcon sx={{fontSize: '80px'}}/></Avatar>
                <List>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Nuova sezione Tariffe disponibile</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Puoi utilizzare gli algoritmi di Intelligenza Artificiale per calcolare i prezzi per i tuoi appartamenti</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Puoi creare prezzi e condizioni adatti secondo la stagionalità della domanda, le festività, i periodi promozionali, etc</ListItem>
                    <ListItem disableGutters><ListItemIcon><VerifiedIcon color="primary"/></ListItemIcon>Puoi modificare tutti i prezzi manualmente a seconda delle tue necessità</ListItem>
                </List>
                <Typography mt={2}>I prezzi non sono visibili pubblicamente ma sono utilizzati per la creazione dei preventivi. </Typography>
            </Stack>
        }
    ], [theme.palette.primary.main]);

    const handleClose = (remindMeLater) => {
        if(!remindMeLater) {
            updateSettings({lastVersionNotified: currentVersion});
        }
        dialog.close();
    }

    // check if a there are new features to notify. Every time the current version changes,
    // check if there are new features to notify. We didn't use the settings.lastVersionNotified
    // as a dependency because we don't want to incour in an infinite loop.
    // Assumption: the globalState.setting provided by the GlobalState component has been
    // already loaded before the FeaturesNotifier component is rendered.
    useEffect(
        () => {
            if(moreRecentThan(currentVersion, lastVersionNotified)) {
                const featuresToNotify = features.filter(feature => moreRecentThan(feature.version, lastVersionNotified));
                setDialogContent(featuresToNotify.map(feature => <React.Fragment key={feature.version}>
                    {feature.messageEl}
                    <Divider/>
                </React.Fragment>));
                dialog.open();
            }
        },
        [currentVersion]  // eslint-disable-line react-hooks/exhaustive-deps
    )

    return <>
        {children}
        {dialog.show && dialogContent.length > 0 && <NewFeatureDialog onClose={handleClose}
                                                                      contentEl={<>{dialogContent}</>} />}
    </>
}

export default FeaturesNotifier;